/**
 * Custom hook to monitor reCAPTCHA script loading status
 * Provides a way to check if the script is loaded without triggering additional loads
 */
import { useState, useEffect } from 'react';
import { log } from '@/lib/utils/logger';

// Global state management for reCAPTCHA script
interface RecaptchaScriptState {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

let globalScriptState: RecaptchaScriptState = {
  isLoaded: false,
  isLoading: false,
  error: null
};

// Subscribers to state changes
const subscribers = new Set<(state: RecaptchaScriptState) => void>();

// Function to update global state and notify subscribers
const updateGlobalState = (newState: Partial<RecaptchaScriptState>) => {
  globalScriptState = { ...globalScriptState, ...newState };
  subscribers.forEach(callback => callback(globalScriptState));
};

// Initialize state based on current DOM/window state
const initializeScriptState = () => {
  if (typeof window === 'undefined') return;

  // Check if script already exists in DOM
  const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
  if (existingScript) {
    log.debug('reCAPTCHA script found in DOM during initialization');
    updateGlobalState({ isLoaded: true, isLoading: false, error: null });
    return;
  }

  // Check if reCAPTCHA is already available globally
  if (window.grecaptcha?.enterprise) {
    log.debug('reCAPTCHA already available globally during initialization');
    updateGlobalState({ isLoaded: true, isLoading: false, error: null });
    return;
  }
};

// Initialize on module load
if (typeof window !== 'undefined') {
  initializeScriptState();
}

/**
 * Hook to monitor reCAPTCHA script loading status
 * @returns Object containing loading state and utility functions
 */
export const useRecaptchaScript = () => {
  const [scriptState, setScriptState] = useState<RecaptchaScriptState>(globalScriptState);

  useEffect(() => {
    // Subscribe to global state changes
    const handleStateChange = (newState: RecaptchaScriptState) => {
      setScriptState(newState);
    };

    subscribers.add(handleStateChange);

    // Initial state check
    if (typeof window !== 'undefined' && !globalScriptState.isLoaded && !globalScriptState.isLoading) {
      initializeScriptState();
    }

    return () => {
      subscribers.delete(handleStateChange);
    };
  }, []);

  // Utility function to manually check script status
  const checkScriptStatus = () => {
    if (typeof window === 'undefined') return false;

    const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
    const isGloballyAvailable = Boolean(window.grecaptcha?.enterprise);
    
    if ((existingScript || isGloballyAvailable) && !globalScriptState.isLoaded) {
      updateGlobalState({ isLoaded: true, isLoading: false, error: null });
      return true;
    }

    return globalScriptState.isLoaded;
  };

  // Function to wait for script to be loaded
  const waitForScript = (timeout: number = 10000): Promise<boolean> => {
    return new Promise((resolve) => {
      if (globalScriptState.isLoaded) {
        resolve(true);
      } else if (globalScriptState.error) {
        resolve(false);
      } else {
        const startTime = Date.now();
        const checkInterval = setInterval(() => {
          if (globalScriptState.isLoaded) {
            clearInterval(checkInterval);
            resolve(true);
          } else if (globalScriptState.error || Date.now() - startTime >= timeout) {
            clearInterval(checkInterval);
            resolve(false);
          }
        }, 100);
      }
    });
  };

  return {
    isLoaded: scriptState.isLoaded,
    isLoading: scriptState.isLoading,
    error: scriptState.error,
    checkScriptStatus,
    waitForScript
  };
};

// Export functions to update global state (for use by RecaptchaLoader)
export const updateRecaptchaScriptState = {
  setLoading: () => updateGlobalState({ isLoading: true, error: null }),
  setLoaded: () => updateGlobalState({ isLoaded: true, isLoading: false, error: null }),
  setError: (error: string) => updateGlobalState({ isLoading: false, error })
};
